import SuiTypography from '@upheal/design-system/components/legacy/Sui/SuiTypography';
import { Box, Stack } from '@mui/material';
import {
    getReferralDiscountedPrice,
    referralDiscountDuration,
} from './PricingCard.data';
import { calculateYearlyDiscount, formatPriceWithCurrency } from '@upheal/billing/utils/payments';
import { DiscountCaption } from '@upheal/billing/DiscountCaption/DiscountCaption';
import { MainPrice } from '@upheal/billing/DisplayPrice/MainPrice';

const OriginalPrice = (props: { price: number; currency?: string }) => {
    const { price, currency = 'USD' } = props;
    return (
        <SuiTypography
            color="secondary"
            fontSize={24}
            lineHeight="normal"
            fontWeight="bold"
            sx={{
                opacity: 0.5,
                textDecoration: 'line-through',
            }}
        >
            {formatPriceWithCurrency(price, currency, true)}
        </SuiTypography>
    );
};

const BillingPeriodCaption = (props: { period: 'monthly' | 'yearly' }) => {
    const { period } = props;

    return (
        <SuiTypography
            variant="caption"
            color="secondary"
            whiteSpace="nowrap"
            fontSize="10px"
            lineHeight="12px"
        >
            /month
            <br />
            billed {period === 'monthly' ? 'monthly' : 'annually'}
        </SuiTypography>
    );
};

const PriceContent = (props: {
    visible?: boolean;
    price: number;
    priceWithYearlyDiscount: number;
    yearlyPricing: boolean;
    showDiscountInfo?: boolean;
    currency?: string;
}) => {
    const {
        visible,
        price,
        priceWithYearlyDiscount,
        yearlyPricing,
        showDiscountInfo = true,
        currency,
    } = props;

    const yearlyDiscount = calculateYearlyDiscount(price, priceWithYearlyDiscount);

    return (
        <Stack
            spacing={1}
            sx={{
                position: 'absolute',
                inset: 0,
                opacity: visible ? 1 : 0,
                transition: 'opacity 0.5s ease',
            }}
        >
            <Stack direction="row" spacing={0.5} alignItems="flex-end">
                {showDiscountInfo && yearlyPricing && (
                    <OriginalPrice price={price} currency={currency} />
                )}
                <MainPrice
                    price={yearlyPricing ? priceWithYearlyDiscount : price}
                    currency={currency}
                />
                <BillingPeriodCaption
                    period={yearlyPricing ? 'yearly' : 'monthly'}
                />
            </Stack>
            {showDiscountInfo && yearlyPricing && (
                <DiscountCaption
                    discount={yearlyDiscount}
                    currency={currency}
                />
            )}
        </Stack>
    );
};

type PricingCardPriceProps = {
    price: number;
    currency?: string;
    priceWithYearlyDiscount: number;
    yearlyPricing?: boolean;
    referralPricing?: boolean;
    showDiscountInfo?: boolean;
    forceHideEmptyYearlySavingsArea?: boolean;
};

export const PricingCardPrice = (props: PricingCardPriceProps) => {
    const {
        price,
        currency,
        priceWithYearlyDiscount,
        yearlyPricing,
        referralPricing,
        showDiscountInfo = true,
        forceHideEmptyYearlySavingsArea,
    } = props;

    const yearlySavingsShown = showDiscountInfo && yearlyPricing;
    const showReferralCaption = referralPricing && !yearlyPricing;

    return (
        <Stack>
            {referralPricing && (
                <SuiTypography
                    variant="caption"
                    color="dark"
                    sx={{
                        opacity: showReferralCaption ? 1 : 0,
                        mb: showReferralCaption ? 1 : 0,
                        height: showReferralCaption ? 34 : 0,
                        transition:
                            'opacity 0.5s ease, margin-bottom 0.25s ease, height 0.25s ease',
                    }}
                >
                    <strong style={{ fontSize: 16 }}>
                        {formatPriceWithCurrency(
                            getReferralDiscountedPrice(price),
                            currency ?? 'USD',
                        )}
                        /month
                    </strong>{' '}
                    for the first {referralDiscountDuration} months, then renews
                    at
                </SuiTypography>
            )}
            <Box
                height={
                    showReferralCaption ||
                    (forceHideEmptyYearlySavingsArea && !yearlySavingsShown)
                        ? 37
                        : 61
                }
                position="relative"
                sx={{
                    transition: 'height 0.25s ease',
                }}
            >
                <PriceContent
                    price={price}
                    currency={currency}
                    priceWithYearlyDiscount={priceWithYearlyDiscount}
                    yearlyPricing={true}
                    visible={yearlyPricing}
                    showDiscountInfo={showDiscountInfo}
                />
                <PriceContent
                    price={price}
                    currency={currency}
                    priceWithYearlyDiscount={priceWithYearlyDiscount}
                    yearlyPricing={false}
                    visible={!yearlyPricing}
                    showDiscountInfo={showDiscountInfo}
                />
            </Box>
        </Stack>
    );
};
